import classnames from "classnames";
import Attachment from "components/base/Attachment/Attachment";
import Button from "components/base/Button/Button";
import Input from "components/base/Input/Input";
import PaymentMethodPicker from "components/base/PaymentMethodPicker/PaymentMethodPicker";
import errorMessage from "constants/errorMessage";
import { COMPANY_FLOW_TYPE, PRL_FLOW_TYPE, PRODUCT_FLOW_TYPE } from "constants/flowTypes";
import { saveAs } from "file-saver";
import useGetData from "hooks/api/useGetData";
import useGetPaymentMethodContent from "hooks/content/useGetPaymentMethodContent";
import useGetPrice from "hooks/utils/useGetPrice";
import { MouseEvent, useState } from "react";
import { Controller, useForm, UseFormReturn } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import {
  DocumentObject,
  EnumPaymentProvider,
  PaymentMethodObject,
  PrlObjectCreateCompanyPrlRequest,
  PrlObjectUpdateCompanyPrlRequest
} from "types/api/api";
import createRegExpFromString from "utils/createRegExpFromString";
import ProductAccordion from "./components/ProductAccordion";
import styles from "./styles.module.scss";
import renderPaymentMethodInput from "./utils/renderPaymentMethodInput";
import { useMutateCreateCompanyPrl } from "api/mutations";
import AlertAmount from "./components/AlertAmount";
import useMutateUpdateCompanyPrl from "api/mutations/useMutateUpdateCompanyPrl";
import extractIdFromUrl from "utils/extractIdFromUrl";
import { useAppContext, useAppDispatch } from "context/AppContext";
import { AxiosError } from "axios";

const FirstScreen = ({ setActiveStepIndex, ...rest }: { setActiveStepIndex: (e: number) => void }) => {
  const { t, i18n } = useTranslation();
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethodObject | null>();
  const { origin } = window.location;
  const dispatch = useAppDispatch();
  const { data, isLoading, prlData, companyData, productData } = useGetData();
  const appContext = useAppContext();
  const { type: flowType, id } = useParams();
  // In order to avoid the ts error, errors prop is set to any
  // eslint-disable-next-line
  type FormType = UseFormReturn<any> & { formState: { isValid: boolean; errors: any } };

  const { mutateAsync: mutateCreateCompanyPrl, isLoading: isLoadingCreateCompany } =
    useMutateCreateCompanyPrl();
  const { mutateAsync: mutateUpdateCompanyPrl } = useMutateUpdateCompanyPrl();

  const {
    control,
    register,
    handleSubmit,
    setValue,
    clearErrors,
    watch,
    formState: { isValid, errors }
  }: FormType = useForm({
    mode: "onChange",
    defaultValues: {
      productQuantity: 1,
      paymentInput: "",
      reference: "",
      amount: ""
    }
  });

  // Watchers
  const paymentInputWatcher = watch("paymentInput");
  const productQuantityWatcher = watch("productQuantity");

  // Attachment handler
  const handleAttachmentClick = (e: MouseEvent, file: DocumentObject) => {
    e.preventDefault();
    let fileName = file?.documentLabel;
    // Manually add extension for .docx mimeType
    if (file?.mimeType === "application/vnd.openxmlformats-officedocument.wordprocessingml.document") {
      fileName = `${fileName}.docx`;
    }
    // Download
    saveAs(
      `${process.env.NODE_ENV === "production" ? origin : "http://localhost:8080"}${file?.documentUrl}`,
      fileName
    );
  };

  // Summary of products prices multiplied by quantity
  const { subtotalPrice } = useGetPrice({ productQuantityWatcher });

  const paymentMethodsContent = useGetPaymentMethodContent({ productQuantityWatcher });

  const onSubmit = async (data: { paymentInput: string; reference: string; amount: string }) => {
    const createCompanyPrlPostData: PrlObjectCreateCompanyPrlRequest = {
      amount: Number(data?.amount),
      nameFirst: "test",
      nameLast: "test",
      reference: data?.reference,
      phonenumber: data?.paymentInput,
      companyName: companyData?.name?.text,
      companyAddress: companyData?.address?.text,
      drawerUuid: id
    };

    const updateCompanyPrlPostData: PrlObjectUpdateCompanyPrlRequest = {
      amount: Number(data?.amount),
      nameFirst: "test",
      nameLast: "test",
      reference: data?.reference,
      phonenumber: data?.paymentInput,
      companyName: companyData?.name?.text,
      companyAddress: companyData?.address?.text
    };

    if (flowType === COMPANY_FLOW_TYPE) {
      const prlUuid = extractIdFromUrl(appContext?.companyPrlUrl);

      let createCompanyPrlResponse;

      if (!appContext?.xUpdateNonce) {
        createCompanyPrlResponse = await mutateCreateCompanyPrl(createCompanyPrlPostData);
      } else {
        await mutateUpdateCompanyPrl(
          {
            payload: updateCompanyPrlPostData,
            id: prlUuid,
            headers: { "x-update-nonce": appContext?.xUpdateNonce }
          },
          {
            onError: async (error) => {
              if (error instanceof AxiosError) {
                // PRL-4015 error code means that xUpdateNonce is invalid
                if (error?.response?.data?.errorCode === "PRL-4015") {
                  // Remove xUpdateNonce value from context
                  dispatch({
                    type: "add",
                    payload: {
                      xUpdateNonce: ""
                    }
                  });

                  // Create new companyPrl instead
                  createCompanyPrlResponse = await mutateCreateCompanyPrl(createCompanyPrlPostData);
                }
              }
            }
          }
        );
      }

      if (createCompanyPrlResponse) {
        const responseData = createCompanyPrlResponse?.data;
        const xUpdateNonce = createCompanyPrlResponse.headers["x-update-nonce"];

        const url = responseData?.url;

        // Save companyPrlUrl and x-update-nonce to context
        dispatch({
          type: "add",
          payload: {
            companyPrlUrl: url,
            xUpdateNonce
          }
        });
      }
    }
    dispatch({
      type: "add",
      payload: data
    });
    setActiveStepIndex(1);
  };

  const onSelectedPaymentMethod = (id: EnumPaymentProvider) => {
    // Reset field value
    setValue("paymentInput", "");
    // Clear errors
    clearErrors("paymentInput");
    if (data) {
      const paymentMethod = data?.paymentMethods?.find((item) => item?.paymentProviderId === id);
      setSelectedPaymentMethod(paymentMethod);
    }
  };

  // useEffect(() => {
  //   if (flowType === COMPANY_FLOW_TYPE && companyData?.isCompanySpecificPrlActive === false) {
  //     return navigate("/404");
  //   }
  // }, [flowType, companyData]);

  return (
    <div {...rest} className={styles.firstScreenWrapper} data-testid="first-screen_wrapper">
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className={`${styles.firstHeader} m-h2`} data-testid="heading-one">
          {t(flowType === COMPANY_FLOW_TYPE ? "firstScreen.companyHeading1" : "firstScreen.heading1")}
        </div>
        {(flowType === PRL_FLOW_TYPE || flowType === PRODUCT_FLOW_TYPE) && (
          <>
            <div className="mt-4">
              <AlertAmount
                paymentMethods={data?.paymentMethods || []}
                currency={prlData?.amount?.currency || productData?.amount?.currency}
                productBasketTotal={subtotalPrice}
              />
            </div>

            <div className={styles.accordionWrapper}>
              {!isLoading && (
                <ProductAccordion
                  productQuantityWatcher={productQuantityWatcher}
                  handleAttachmentClick={handleAttachmentClick}
                  inputComponent={
                    <Controller
                      name="productQuantity"
                      control={control}
                      render={({ field }) => <input style={{ width: "100%" }} {...field} />}
                    />
                  }
                  productSummary={subtotalPrice}
                />
              )}
            </div>
          </>
        )}

        {flowType === COMPANY_FLOW_TYPE &&
          companyData?.firstScreenFields.map((field) => {
            return (
              <>
                {field && (
                  <div className={styles.fieldWrapper} key={field.name}>
                    <Input
                      id={field.name}
                      dataTestId={field.name}
                      message={errors[field.name] && errors[field.name].message}
                      mandatory={field.mandatory}
                      error={!!errors[field.name]}
                      defaultValue={field?.value}
                      label={field?.label?.find((l) => l.lang === i18n.language)?.text}
                      placeholder={field?.description?.find((l) => l.lang === i18n.language)?.text}
                      {...register(field.name, {
                        required: {
                          value: field?.mandatory || false,
                          message: errorMessage.REQUIRED
                        },
                        pattern: {
                          value: createRegExpFromString(field?.regex),
                          message: errorMessage.PATTERN
                        }
                      })}
                    />
                  </div>
                )}
              </>
            );
          })}

        <div className="d-flex align-items-center justify-content-center">
          <div className="m-container-medium">
            {/* Display attachments outside of accordion for flowType = prl */}
            {flowType === PRL_FLOW_TYPE && (
              <div className="mt-5" data-testid="attachment-sector">
                <div className="m-h5">{t("firstScreen.attachments")}</div>
                <Attachment items={prlData?.attachments} onClick={handleAttachmentClick} />
              </div>
            )}

            <div className="m-divider"></div>

            <div className="m-h2" data-testid="heading-two">
              {t("firstScreen.heading2")}
            </div>
            <div className={styles.paymentMethodContainer}>
              <Controller
                name="paymentMethod"
                control={control}
                render={({ field }) => (
                  <PaymentMethodPicker
                    onSelect={onSelectedPaymentMethod}
                    items={paymentMethodsContent}
                    {...field}
                  />
                )}
              />
            </div>
            <div
              data-testid="mobile_money_phone-wrapper"
              className={classnames(styles.mobileMoneyWrapper, {
                [styles.mobileMoneyWrapperVisible]: !!selectedPaymentMethod
              })}
            >
              {!isLoading &&
                renderPaymentMethodInput({
                  control,
                  errors,
                  i18n,
                  register,
                  selectedPaymentMethod
                })}
            </div>
            <div className={styles.buttonWrapper}>
              <Button
                variant="primary"
                dataTestId={"submit-button"}
                type="submit"
                disabled={!paymentInputWatcher || !isValid}
                isLoading={flowType === COMPANY_FLOW_TYPE && isLoadingCreateCompany}
                title={
                  flowType === COMPANY_FLOW_TYPE
                    ? t("firstScreen.buttonTitle2")
                    : t("firstScreen.buttonTitle1")
                }
              />
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default FirstScreen;
